import { Link } from "wouter";
import { ChevronRight, Menu, Moon, Sun } from "lucide-react";
import { cn } from "@/lib/utils";
import { useBreadcrumbTrail } from "@/components/layout/breadcrumbs";

interface TopBarProps {
  /** Opens the phone menu drawer (button shown below `lg`). */
  onMenuClick: () => void;
  theme: "light" | "dark";
  onThemeToggle: () => void;
}

/**
 * The CMS top bar (C-CMS-DS): "where am I" breadcrumb (all levels; the last crumb only
 * below `lg`) and the light/dark switch. Sign-out lives in the menu footer.
 */
export default function TopBar({ onMenuClick, theme, onThemeToggle }: TopBarProps) {
  const trail = useBreadcrumbTrail();
  const iconButton =
    "inline-flex h-8 min-w-8 flex-none items-center justify-center rounded-lg border border-ds-line bg-ds-panel px-2 text-ds-ink2 transition-colors hover:bg-ds-sunk hover:text-ds-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-accent";

  return (
    <header
      role="banner"
      className="z-40 flex h-14 flex-none items-center gap-3 border-b border-ds-line bg-ds-panel px-4 font-ds lg:px-6"
    >
      <button type="button" onClick={onMenuClick} aria-label="Open menu" className={cn(iconButton, "lg:hidden")}>
        <Menu className="h-4 w-4" aria-hidden="true" />
      </button>

      {trail && trail.length > 0 && (
        <nav aria-label="Breadcrumb" className="min-w-0">
          <ol className="flex min-w-0 items-center gap-1.5 text-[13px] text-ds-ink3">
            {trail.map((crumb, index) => {
              const isLast = index === trail.length - 1;
              return (
                <li key={`${index}-${crumb.label}`} className={cn("flex min-w-0 items-center gap-1.5", !isLast && "hidden lg:flex")}>
                  {isLast ? (
                    <b aria-current="page" className="truncate font-semibold text-ds-ink">
                      {crumb.label}
                    </b>
                  ) : crumb.href ? (
                    <Link href={crumb.href} className="whitespace-nowrap text-ds-ink3 transition-colors hover:text-ds-ink">
                      {crumb.label}
                    </Link>
                  ) : (
                    <span className="whitespace-nowrap">{crumb.label}</span>
                  )}
                  {!isLast && <ChevronRight className="h-3 w-3 flex-none" aria-hidden="true" />}
                </li>
              );
            })}
          </ol>
        </nav>
      )}

      <span className="flex-1" />

      <button
        type="button"
        onClick={onThemeToggle}
        aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
        className={iconButton}
      >
        {theme === "dark" ? <Sun className="h-[15px] w-[15px]" aria-hidden="true" /> : <Moon className="h-[15px] w-[15px]" aria-hidden="true" />}
      </button>
    </header>
  );
}
